import { IconButton, InputBase, Paper } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useRef, useState } from "react";

import _ from "lodash";

interface Props {
  doSearch: (searchTerm: string) => void; // Called when the search form is submitted
  setSearchTerm: Function; // Called when the search term changes
  searchTerm: string; // Current value of the search term
  onReset: Function; // Called to reload all the products
  placeholderText?: string;
  selfFocus?: boolean; // If true the component will attempt to steal focus
  noAutoSubmit?: boolean; // It true disable auto submit of the search after 2s
  disabled?: boolean; // Component is disabled, but shown
  clearOnEnter: boolean; // If false enter will not clear the field, leaving the value intact
  focusRef?: React.MutableRefObject<HTMLInputElement | null>; // Populated with the input element so callers can refocus it
}
const Search = (props: Props) => {
  const [localSearchTerm, setLocalSearchTerm] = useState<string>("");
  const isMounted = useRef(false);

  const handleTextEntry = (e) => {
    setLocalSearchTerm(e.target.value.replace(/^\s*/, ""));
  };

  useEffect(() => {
    setLocalSearchTerm(props.searchTerm);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Skip on initial mount — onReset should only fire when the user actively clears the field
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (localSearchTerm.length >= 3 && !props.noAutoSubmit) {
      const delayDebounceFn = setTimeout(() => {
        props.doSearch(localSearchTerm);
      }, 1000);

      return () => clearTimeout(delayDebounceFn);
    } else if (localSearchTerm.length === 0) {
      props.onReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearchTerm]);

  const handleSubmit = (e) => {
    e.preventDefault();
    props.doSearch(localSearchTerm);
    setLocalSearchTerm("");
  };

  const placeholder =
    props.placeholderText ||
    "Search...";
  return (
    <Paper
      component="form"
      sx={{ p: "2px 4px", display: "flex", alignItems: "center" }}
      onSubmit={handleSubmit}
    >
      <InputBase
        sx={{ ml: 1, flex: 1 }}
        placeholder={placeholder}
        inputProps={{ "aria-label": "search" }}
        autoFocus={true}
        autoComplete="off"
        value={localSearchTerm}
        onChange={handleTextEntry}
        onKeyPress={(e) => props.clearOnEnter === false && e.key === "Enter" && e.preventDefault()}
        inputRef={(input) => {
          if (props.focusRef) props.focusRef.current = input;
          if (input && props.selfFocus) input.focus();
        }}
        disabled={props.disabled || false}
      />
      {_.isEmpty(localSearchTerm) && (
        <IconButton
          type="submit"
          sx={{ p: "10px" }}
          aria-label="search"
          disabled={props.disabled || false}
        >
          <SearchIcon />
        </IconButton>
      )}
      {!_.isEmpty(localSearchTerm) && (
        <IconButton
          sx={{ p: "10px" }}
          aria-label="clear"
          disabled={props.disabled || false}
          onClick={() => {
            setLocalSearchTerm("");
            props.setSearchTerm("");
            props.onReset();
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Paper>
  );
};

export default Search;
